<script setup>
import { computed } from 'vue'
import { Head, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { platformLabel } from '@/lib/platformLabel'
import LuxButton from '@/Components/Public/LuxButton.vue'
import heroSunset from '@/images/home/hero-sunset.webp'
import archCatamaran from '@/images/home/arch-catamaran.webp'
import villaDusk from '@/images/home/villa-dusk.webp'
import PaymentMethods from '@/Components/Public/PaymentMethods.vue'
import DateField from '@/Components/Public/DateField.vue'
import TrustRow from '@/Components/Public/TrustRow.vue'

// Private-charter home ("Sunlit" direction, chosen 2026-09-04). The page
// runs light and warm: a sunset-over-sea hero, a planner card, the five
// occasions, the fleet, a night-panel concierge desk, the ashore panels (villa + transfers),
// eco/culture experiences, trust, and the channels. Fed by existing listings
// (private/VIP/luxury tours as the fleet) until a Charter model exists.
defineOptions({ layout: PublicLayout })

const props = defineProps({
  prefix:      { type: String, default: '' },
  fleet:       { type: Array,  default: () => [] },
  experiences: { type: Array,  default: () => [] },
  villa:       { type: Object, default: null },
  transfers_from: { type: String, default: null },
  occasions:   { type: Array,  default: () => [] }, // every occasion, hub order (family first)
  destinations: { type: Array, default: () => [] }, // [{slug, title}] the destination pages, for the strip under the fleet
})

const { t } = useI18n()
const page = usePage()
const locale       = computed(() => page.props.locale || 'en')
const partnersBase = computed(() => page.props.partners_base || 'https://partners.getphuket.com')
const writeUs      = computed(() => page.props.brand?.channels?.message || [])

// Locale-prefixed path on whichever surface we're on (brand host or /_p).
function path(p) {
  const base = props.prefix || ''
  const loc  = locale.value
  if (!loc || loc === 'en') return `${base}${p}`
  return `${base}/${loc}${p === '/' ? '' : p}`
}

const OCCASIONS = ['celebration', 'romantic', 'corporate', 'wedding', 'multi_day']
const today = new Date().toISOString().slice(0, 10)

// One pill, Premium, for the premium and luxury tiers; standard → no pill. The
// gold VIP pill was retired on 2026-09-29: every listing is private already.
function fleetBadge(item) {
  return (item.luxury || item.tour_type === 'vip' || ['premium', 'luxury'].includes(item.tier)) ? 'premium' : null
}
function hours(minutes) {
  return minutes ? Math.max(1, Math.round(minutes / 60)) : null
}
function guideLangs(item) {
  return (item.guide_languages || []).map(l => l.toUpperCase()).join(' · ')
}
function priceUnit(item) {
  if (item.kind === 'charter') return t('public.charter_home.fleet.whole_boat')
  if (item.pricing_model !== 'per_session') return t('public.charter_home.fleet.per_person')
  return ['private', 'vip'].includes(item.tour_type)
    ? t('public.charter_home.fleet.per_group')
    : t('public.charter_home.fleet.per_session')
}
function fleetHref(item) {
  return item.kind === 'charter' ? path(`/charters/${item.id}`) : hostLink('experiences', `/tours/${item.id}`)
}

// Link into a vertical that lives on its own subdomain since the split
// (villas / rentals / transfers / experiences); the /_p preview keeps every
// vertical local, so the shared base collapses to the prefix + section.
const HOST_SECTIONS = { villas: '/property', rentals: '/rentals', transfers: '/transfers', experiences: '/tours' }
function hostLink(kind, tail = '/') {
  const base = (page.props.public_hosts || {})[kind] || ''
  const p    = tail === '/' ? '' : tail
  if (!base || base.startsWith('/')) return path(p || HOST_SECTIONS[kind] || '/')
  const loc = locale.value
  return `${base}${(!loc || loc === 'en') ? '' : `/${loc}`}${p}` || base
}


const villaPhoto = computed(() => props.villa?.photo_url || villaDusk)
// villas live on villas.getphuket.com since the split (the /_p preview keeps
// everything local — the shared base is then the /_p prefix itself).
const villaHref = computed(() => {
  const hosts = page.props.public_hosts || {}
  const base  = hosts.villas || ''
  const tail  = props.villa ? `/property/${props.villa.id}` : ''
  if (!base || base.startsWith('/')) return props.villa ? path(`/property/${props.villa.id}`) : path('/property')
  const loc = locale.value
  return `${base}${(!loc || loc === 'en') ? '' : `/${loc}`}${tail}` || base
})
</script>

<template>
  <Head>
    <title>{{ t('public.charter_home.head_title') }}</title>
    <meta name="description" :content="t('public.charter_home.head_description')">
    <meta property="og:title"       :content="t('public.charter_home.head_title')">
    <meta property="og:description" :content="t('public.charter_home.head_description')">
    <meta property="og:type"        content="website">
  </Head>

  <!-- HERO — sunset over the sea, full bleed -->
  <header class="hero">
    <div class="hero__photo" :style="{ backgroundImage: `url(${heroSunset})` }" role="img" :aria-label="t('public.charter_home.hero.eyebrow')" />
    <div class="hero__scrim" aria-hidden="true" />
    <div class="hero__inner">
      <p class="hero__eyebrow">{{ t('public.charter_home.hero.eyebrow') }}</p>
      <h1 class="hero__title">{{ t('public.charter_home.hero.headline') }} <em>{{ t('public.charter_home.hero.headline_em') }}</em></h1>
      <p class="hero__lede">{{ t('public.charter_home.hero.lede') }}</p>
      <div class="hero__ctas">
        <LuxButton :href="path('/plan')" variant="solid" tone="dark" glyph="arrow">{{ t('public.charter_home.hero.cta_plan') }}</LuxButton>
        <LuxButton :href="path('/charters')" variant="line" tone="dark">{{ t('public.charter_home.hero.cta_fleet') }}</LuxButton>
      </div>
    </div>
  </header>

  <!-- PLANNER — overlaps the hero; a plain GET so it works without JS -->
  <div class="planner">
    <form class="planner__card" :action="path('/plan')" method="get">
      <label class="planner__f">
        <span class="planner__label">{{ t('public.charter_home.planner.occasion') }}</span>
        <select name="occasion" class="planner__input">
          <option value="">{{ t('public.charter_home.planner.pick') }}</option>
          <option v-for="o in (occasions.length ? occasions : OCCASIONS)" :key="o" :value="o">{{ t(`public.plan.occasions.${o}`) }}</option>
        </select>
      </label>
      <label class="planner__f">
        <span class="planner__label">{{ t('public.charter_home.planner.date') }}</span>
        <DateField name="start_on" class="planner__input" :min="today" :placeholder="t('public.pick_date')" />
      </label>
      <label class="planner__f">
        <span class="planner__label">{{ t('public.charter_home.planner.guests') }}</span>
        <input name="adults" type="number" class="planner__input" min="1" max="100" value="2">
      </label>
      <div class="planner__go">
        <LuxButton type="submit" variant="solid" tone="light" glyph="arrow">{{ t('public.charter_home.planner.submit') }}</LuxButton>
      </div>
    </form>
    <div class="planner__trust"><TrustRow /></div>
  </div>

  <!-- OCCASIONS -->
  <section class="sec">
    <div class="inner">
      <div class="sec__head">
        <div>
          <p class="eyebrow">{{ t('public.charter_home.occasions.title') }}</p>
          <h2 class="h2">{{ t('public.charter_home.occasions.heading') }}</h2>
        </div>
        <a :href="path('/occasions')" class="more">{{ t('public.charter_home.occasions.all') }} →</a>
      </div>
      <ul class="occ">
        <li v-for="(o, i) in OCCASIONS" :key="o">
          <a :href="path(`/occasions/${o}`)" class="occ__card">
            <span class="occ__n">0{{ i + 1 }}</span>
            <h3 class="occ__title">{{ t(`public.charter_home.occasions.items.${o}.title`) }}</h3>
            <p class="occ__sub">{{ t(`public.charter_home.occasions.items.${o}.sub`) }}</p>
            <span class="occ__arrow" aria-hidden="true">→</span>
          </a>
        </li>
      </ul>
    </div>
  </section>

  <!-- FLEET -->
  <section class="sec sec--tight">
    <div class="inner">
      <div class="sec__head">
        <div>
          <p class="eyebrow">{{ t('public.charter_home.fleet.title') }}</p>
          <h2 class="h2">{{ t('public.charter_home.fleet.heading') }}</h2>
          <p class="sec__sub">{{ t('public.charter_home.fleet.sub') }}</p>
        </div>
        <a :href="path('/charters')" class="more">{{ t('public.charter_home.fleet.all') }} →</a>
      </div>

      <ul v-if="fleet.length" class="boats">
        <li v-for="b in fleet" :key="b.id">
          <a :href="fleetHref(b)" class="boat">
            <div class="boat__photo" :class="{ 'boat__photo--empty': !b.photo_url }" :style="b.photo_url ? { backgroundImage: `url(${b.photo_url})` } : null">
              <span v-if="fleetBadge(b)" class="badge" :class="`badge--${fleetBadge(b)}`">{{ t(`public.charter_home.fleet.${fleetBadge(b)}`) }}</span>
            </div>
            <div class="boat__body">
              <h3 class="boat__title">{{ b.title }}</h3>
              <p class="boat__spec mono">
                <span v-if="b.kind === 'charter'">{{ t(`public.charters.vessel_types.${b.vessel_type}`) }}<template v-if="b.length_ft"> · {{ t('public.charters.ft', { n: b.length_ft }) }}</template></span>
                <span v-if="hours(b.duration_minutes)">{{ t('public.charter_home.fleet.hours', { n: hours(b.duration_minutes) }) }}</span>
                <span v-if="b.max_group_size">{{ t('public.charter_home.fleet.up_to', { n: b.max_group_size }) }}</span>
                <span v-if="guideLangs(b)">{{ t('public.charter_home.fleet.guide', { langs: guideLangs(b) }) }}</span>
              </p>
              <p class="boat__price">
                <template v-if="b.price_on_request"><b>{{ t('public.charter_home.fleet.on_request') }}</b></template>
                <template v-else>
                  <span>{{ t('public.charter_home.fleet.from') }}</span> <b>{{ b.card_price }}</b> <span>{{ priceUnit(b) }}</span>
                </template>
              </p>
            </div>
          </a>
        </li>
      </ul>
      <p v-else class="empty">
        {{ t('public.charter_home.fleet.empty') }}
        <a :href="path('/plan')" class="more">{{ t('public.charter_home.hero.cta_plan') }} →</a>
      </p>
      <nav v-if="destinations.length" class="dest" :aria-label="t('public.charter_home.destinations.title')">
        <span class="dest__label">{{ t('public.charter_home.destinations.heading') }}</span>
        <a v-for="d in destinations" :key="d.slug" :href="path(`/destinations/${d.slug}`)" class="dest__chip">{{ d.title }}</a>
        <a :href="path('/destinations')" class="more">{{ t('public.charter_home.destinations.all') }} →</a>
      </nav>
    </div>
  </section>

  <!-- CONCIERGE — the private desk: one deliberate night panel between the light sections -->
  <section class="cx">
    <div class="inner cx__grid">
      <div class="cx__body">
        <p class="cx__eyebrow"><i /><span>{{ t('public.charter_home.concierge.title') }}</span></p>
        <h2 class="cx__title">{{ t('public.charter_home.concierge.heading') }}</h2>
        <p class="cx__lede">{{ t('public.charter_home.concierge.lede') }}</p>

        <ol class="cx__steps">
          <li v-for="(k, i) in ['one', 'two', 'three']" :key="k" class="cx__step">
            <span class="cx__n">0{{ i + 1 }}</span>
            <div>
              <h3 class="cx__step-title">{{ t(`public.charter_home.concierge.steps.${k}.title`) }}</h3>
              <p class="cx__step-sub">{{ t(`public.charter_home.concierge.steps.${k}.sub`) }}</p>
            </div>
          </li>
        </ol>

        <ul class="cx__facts">
          <li v-for="k in ['hours', 'languages', 'price', 'licensed']" :key="k"><i /><span>{{ t(`public.charter_home.concierge.facts.${k}`) }}</span></li>
        </ul>

        <div class="cx__ctas">
          <LuxButton :href="path('/plan')" variant="solid" tone="dark" glyph="arrow">{{ t('public.charter_home.concierge.cta') }}</LuxButton>
          <template v-if="writeUs.length">
            <span class="cx__or">{{ t('public.charter_home.concierge.write_direct') }}</span>
            <LuxButton v-for="c in writeUs" :key="c.id" :href="c.url" variant="line" tone="dark" :glyph="c.platform" target="_blank" rel="noopener">{{ platformLabel(c.platform) }}</LuxButton>
          </template>
        </div>
        <p class="cx__sub">{{ t('public.charter_home.concierge.cta_sub') }}</p>
      </div>

      <figure class="cx__figure">
        <div class="cx__arch" :style="{ backgroundImage: `url(${archCatamaran})` }" role="img" :aria-label="t('public.charter_home.fleet.title')" />
        <figcaption class="cx__sig">{{ t('public.charter_home.concierge.signature') }}</figcaption>
      </figure>
    </div>
  </section>

  <!-- ASHORE — the villa for the nights and the car in between (full top padding: it follows the dark concierge panel) -->
  <section class="sec">
    <div class="inner">
      <div class="sec__head">
        <div>
          <p class="eyebrow">{{ t('public.charter_home.stay.eyebrow') }}</p>
          <h2 class="h2">{{ t('public.charter_home.stay.heading') }}</h2>
        </div>
        <p class="sec__lede">{{ t('public.charter_home.stay.lede') }}</p>
      </div>
      <div class="ashore">
        <article class="villa">
          <div class="villa__photo" :style="{ backgroundImage: `url(${villaPhoto})` }" role="img" :aria-label="villa?.title || t('public.charter_home.villas.label')" />
          <div class="villa__scrim" aria-hidden="true" />
          <div class="villa__txt">
            <p class="panel__label"><i /><span>{{ t('public.charter_home.villas.label') }}</span></p>
            <h3 class="panel__title">{{ t('public.charter_home.villas.heading') }}</h3>
            <p class="panel__sub">{{ t('public.charter_home.villas.sub') }}</p>
            <ul class="panel__facts">
              <li v-for="k in ['view', 'staff', 'walked']" :key="k"><i /><span>{{ t(`public.charter_home.villas.facts.${k}`) }}</span></li>
            </ul>
            <div class="panel__ctas">
              <LuxButton :href="villaHref" variant="solid" tone="dark" glyph="arrow">{{ t('public.charter_home.villas.cta') }}</LuxButton>
              <span v-if="villa?.price_per_night" class="panel__from">{{ t('public.charter_home.villas.from', { price: villa.price_per_night }) }}<template v-if="villa.area"> · {{ villa.area }}</template></span>
            </div>
          </div>
        </article>

        <article class="ride">
          <p class="panel__label panel__label--ink"><i /><span>{{ t('public.charter_home.transfers.label') }}</span></p>
          <h3 class="panel__title panel__title--ink">{{ t('public.charter_home.transfers.heading') }}</h3>
          <p class="panel__sub panel__sub--ink">{{ t('public.charter_home.transfers.sub') }}</p>
          <ol class="route">
            <li v-for="s in ['airport', 'stay', 'marina']" :key="s">
              <i />
              <div>
                <span class="route__stop">{{ t(`public.charter_home.transfers.route.${s}`) }}</span>
                <span class="route__note">{{ t(`public.charter_home.transfers.route.${s}_sub`) }}</span>
              </div>
            </li>
          </ol>
          <ul class="panel__facts panel__facts--ink">
            <li v-for="k in ['meet', 'cars', 'same']" :key="k"><i /><span>{{ t(`public.charter_home.transfers.facts.${k}`) }}</span></li>
          </ul>
          <div class="panel__ctas">
            <LuxButton :href="hostLink('transfers')" variant="solid" tone="light" glyph="arrow">{{ t('public.charter_home.transfers.cta') }}</LuxButton>
            <span v-if="transfers_from" class="panel__from panel__from--ink">{{ t('public.charter_home.transfers.from', { price: transfers_from }) }}</span>
          </div>
        </article>
      </div>
    </div>
  </section>

  <!-- EXPERIENCES — eco & culture (hidden entirely when nothing is live) -->
  <section v-if="experiences.length" class="sec sec--tight">
    <div class="inner">
      <div class="sec__head">
        <div>
          <p class="eyebrow">{{ t('public.charter_home.experiences.title') }}</p>
          <h2 class="h2">{{ t('public.charter_home.experiences.heading') }}</h2>
        </div>
        <a :href="hostLink('experiences')" class="more">{{ t('public.charter_home.experiences.all') }} →</a>
      </div>
      <ul v-if="experiences.length" class="exps">
        <li v-for="e in experiences" :key="e.id">
          <a :href="hostLink('experiences', `/tours/${e.id}`)" class="exp">
            <div class="exp__frame">
              <div class="exp__photo" :class="{ 'exp__photo--empty': !e.photo_url }" :style="e.photo_url ? { backgroundImage: `url(${e.photo_url})` } : null" />
            </div>
            <p class="exp__meta"><i /><span>{{ t(`public.experiences.categories.${e.category}`) }}<template v-if="e.duration_minutes"> · {{ Math.round(e.duration_minutes / 60) }}h</template></span></p>
            <h3 class="exp__title">{{ e.title }}</h3>
            <p v-if="e.summary" class="exp__sub">{{ e.summary }}</p>
            <p class="exp__foot">
              <span v-if="e.card_price" class="exp__price">{{ t('public.charter_home.experiences.from', { price: e.card_price }) }}</span>
              <span class="exp__go">{{ t('public.charter_home.experiences.view') }} →</span>
            </p>
          </a>
        </li>
      </ul>
      <p v-else class="empty">{{ t('public.charter_home.experiences.empty') }}</p>
    </div>
  </section>

  <!-- HALLMARKS -->
  <section class="sec sec--tight">
    <div class="inner">
      <ul class="marks">
        <li v-for="k in ['licensed', 'fleet', 'chat', 'pay']" :key="k" class="mark">
          <i />
          <h3 class="mark__title">{{ t(`public.charter_home.trust.${k}`) }}</h3>
          <p class="mark__sub">{{ t(`public.charter_home.trust_sub.${k}`) }}</p>
        </li>
      </ul>
    </div>
  </section>

  <!-- WE ACCEPT -->
  <section class="sec sec--tight">
    <div class="inner">
      <div class="accept"><PaymentMethods /></div>
    </div>
  </section>

  <!-- CONTACT -->
  <section v-if="writeUs.length" class="sec sec--tight">
    <div class="inner">
      <div class="contact">
        <p class="contact__eyebrow"><i /><span>{{ t('public.charter_home.contact.eyebrow') }}</span><i /></p>
        <h2 class="h2 contact__title">{{ t('public.charter_home.contact.heading') }}</h2>
        <p class="contact__sub">{{ t('public.charter_home.contact.sub') }}</p>
        <div class="contact__chans">
          <LuxButton v-for="(c, i) in writeUs" :key="c.id" :href="c.url" :variant="i === 0 ? 'solid' : 'line'" tone="light" :glyph="c.platform" target="_blank" rel="noopener">{{ t('public.write_on', { platform: platformLabel(c.platform) }) }}</LuxButton>
          <LuxButton :href="path('/plan')" variant="line" tone="light" glyph="arrow">{{ t('public.charter_home.concierge.cta') }}</LuxButton>
        </div>
      </div>
    </div>
  </section>

  <!-- PARTNER line -->
  <section class="partner">
    <div class="inner partner__inner">
      <span>{{ t('public.partners.home_band_text') }}</span>
      <a :href="partnersBase" class="more">{{ t('public.partners.home_band_cta') }}</a>
    </div>
  </section>
</template>

<style scoped>
/* ---- layout primitives ---------------------------------------------------- */
.inner { max-width: 80rem; margin: 0 auto; padding-inline: clamp(1.25rem, 4vw, 3rem); }
.sec { padding-block: var(--section-y); }
.sec--tight { padding-top: 0; }
.sec__head { display: grid; grid-template-columns: 1fr auto; gap: 1.5rem; align-items: end; margin-bottom: clamp(1.5rem, 3vw, 2.5rem); }
.sec__sub { margin: 0.85rem 0 0; color: var(--inkSoft); max-width: 52ch; }
.eyebrow { margin: 0; font-size: 0.75rem; letter-spacing: var(--eyebrow-tracking); text-transform: uppercase; font-weight: 700; color: var(--accent); }
.h2 { font-family: var(--font-display, var(--display)); font-weight: 500; letter-spacing: -0.005em; font-size: clamp(2rem, 3.6vw, 3.1rem); line-height: 1.05; margin: 0.55rem 0 0; color: var(--ink); text-wrap: balance; }
.more { font-size: 0.9rem; font-weight: 700; color: var(--azure, var(--second)); text-decoration: none; white-space: nowrap; }
.more:hover { text-decoration: underline; }
.mono { font-family: var(--font-mono, var(--mono)); }
.empty { color: var(--inkSoft); display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; }

.btn { display: inline-flex; align-items: center; gap: 0.6rem; padding: 0.85rem 1.5rem; border-radius: 999px; font-weight: 700; font-size: 0.95rem; text-decoration: none; border: 1px solid var(--line); background: var(--surface); color: var(--ink); cursor: pointer; font-family: inherit; transition: transform 150ms ease, box-shadow 200ms ease, filter 150ms ease; }
.btn:hover { transform: translateY(-1px); box-shadow: 0 12px 24px -14px color-mix(in oklch, var(--ink) 40%, transparent); }
.btn--primary { background: var(--accent); color: var(--accentInk); border-color: var(--accent); }
.btn--primary:hover { filter: brightness(0.96); }
.btn--light { background: rgba(255, 255, 255, 0.94); color: var(--night, #0B2537); border-color: transparent; }
.btn--ghost { background: var(--surface); }

/* ---- hero ----------------------------------------------------------------- */
.hero { position: relative; min-height: clamp(30rem, 72vh, 42rem); display: flex; align-items: flex-end; overflow: hidden; color: #fff; background: var(--night, #0B2537); }
.hero__photo { position: absolute; inset: 0; background-size: cover; background-position: center 64%; }
.hero__scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(11, 37, 55, 0.2) 0%, rgba(11, 37, 55, 0) 38%, rgba(11, 37, 55, 0.66) 100%); }
.hero__inner { position: relative; width: 100%; max-width: 80rem; margin: 0 auto; padding: 7rem clamp(1.25rem, 4vw, 3rem) 7.5rem; }
.hero__eyebrow { margin: 0; font-size: 0.75rem; letter-spacing: var(--eyebrow-tracking); text-transform: uppercase; font-weight: 700; color: #FFD9C4; }
.hero__title { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: clamp(2.9rem, 6.6vw, 5.75rem); line-height: 0.96; letter-spacing: -0.005em; margin: 1rem 0 0; max-width: 15ch; color: #fff; text-wrap: balance; text-shadow: 0 2px 30px rgba(11, 37, 55, 0.35); }
.hero__title em { font-style: italic; color: #FFE1CF; }
.hero__lede { margin: 1.4rem 0 0; font-size: 1.1rem; line-height: 1.55; color: rgba(255, 255, 255, 0.92); max-width: 46ch; }
.hero__ctas { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 1.9rem; }

/* ---- planner -------------------------------------------------------------- */
.planner { position: relative; z-index: 2; max-width: 80rem; margin: -3.75rem auto 0; padding-inline: clamp(1.25rem, 4vw, 3rem); }
/* The countable facts sit right under the planner, before any product. */
.planner__trust { margin-top: 1rem; padding: 0.35rem 0.25rem 0; }
.planner__card { display: grid; grid-template-columns: 1.2fr 1fr 0.8fr auto; background: var(--surface); border: 1px solid var(--line); border-radius: 1.25rem; box-shadow: 0 30px 60px -30px color-mix(in oklch, var(--ink) 45%, transparent); overflow: hidden; }
.planner__f { display: grid; gap: 0.25rem; padding: 1rem 1.35rem; border-right: 1px solid var(--line); min-width: 0; }
.planner__label { font-size: 0.68rem; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; color: var(--muted); }
.planner__input { border: 0; background: transparent; padding: 0.15rem 0; font: inherit; font-weight: 600; color: var(--ink); width: 100%; min-width: 0; }
.planner__input:focus { outline: none; box-shadow: 0 2px 0 var(--second); }
select.planner__input { color-scheme: dark; } /* the native option list renders dark too, so the cream ink stays readable (2026-10-24) */
.planner__go { display: flex; align-items: center; padding: 0.75rem; }

/* ---- occasions ------------------------------------------------------------ */
.occ { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(5, 1fr); gap: 1rem; }
.occ__card { position: relative; display: flex; flex-direction: column; gap: 0.5rem; height: 100%; box-sizing: border-box; padding: 1.5rem 1.4rem 1.5rem; background: var(--surface); border: 1px solid var(--line); border-radius: 1.1rem; text-decoration: none; color: inherit; transition: border-color 200ms ease, transform 150ms ease, box-shadow 200ms ease; }
.occ__card::before { content: ""; position: absolute; top: 0; left: 1.4rem; right: 1.4rem; height: 1px; background: linear-gradient(90deg, var(--gold) 0%, transparent 100%); opacity: 0.8; }
.occ__card:hover { border-color: color-mix(in oklch, var(--gold) 70%, var(--line)); transform: translateY(-3px); box-shadow: 0 24px 40px -30px color-mix(in oklch, var(--ink) 50%, transparent); }
.occ__n { font-family: var(--font-display, var(--display)); font-size: 1.05rem; line-height: 1; color: var(--gold); letter-spacing: 0.04em; }
.occ__title { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: 1.7rem; line-height: 1; margin: 0; color: var(--ink); }
.occ__sub { margin: 0; font-size: 0.85rem; line-height: 1.45; color: var(--inkSoft); }
.occ__arrow { font-family: var(--font-display, var(--display)); font-size: 1.4rem; line-height: 1; color: var(--gold); margin-top: auto; padding-top: 0.75rem; align-self: flex-end; } /* pinned to the bottom-right corner so the five arrows share one baseline */

/* ---- where to go: the destination pages, one chip each, under the fleet ---- */
.dest { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; margin-top: 1.6rem; padding-top: 1.2rem; border-top: 1px solid var(--line); }
.dest__label { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); font-weight: 700; margin-right: 0.35rem; }
.dest__chip { display: inline-flex; padding: 0.5rem 0.95rem; border-radius: 999px; background: var(--surface); border: 1px solid var(--line); color: var(--ink); font-size: 0.88rem; font-weight: 600; text-decoration: none; transition: border-color 150ms ease; }
.dest__chip:hover { border-color: color-mix(in oklch, var(--gold) 70%, var(--line)); }
.dest .more { margin-left: auto; }

/* ---- fleet ---------------------------------------------------------------- */
.boats { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.4rem; }
.boat { display: flex; flex-direction: column; height: 100%; background: var(--surface); border: 1px solid var(--line); border-radius: 1.4rem; overflow: hidden; text-decoration: none; color: inherit; transition: transform 150ms ease, box-shadow 200ms ease; }
.boat:hover { transform: translateY(-3px); box-shadow: 0 24px 40px -24px color-mix(in oklch, var(--ink) 40%, transparent); }
.boat__photo { aspect-ratio: 4 / 3; background-size: cover; background-position: center; position: relative; }
.boat__photo--empty, .exp__photo--empty { background: linear-gradient(160deg, var(--sky, var(--secondSoft)) 0%, var(--second) 100%); }
.badge { position: absolute; top: 0.9rem; left: 0.9rem; font-family: var(--font-mono, var(--mono)); font-size: 0.68rem; letter-spacing: 0.1em; text-transform: uppercase; padding: 0.35rem 0.65rem; border-radius: 999px; background: rgba(255, 255, 255, 0.92); color: var(--azure, var(--second)); }
.badge--luxury { color: #8A6516; background: var(--goldSoft, #FBF0D4); }
.boat__body { padding: 1.25rem 1.4rem 1.4rem; display: flex; flex-direction: column; gap: 0.5rem; flex: 1 1 auto; }
.boat__title { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: 1.7rem; line-height: 1.05; margin: 0; color: var(--ink); }
.boat__spec { margin: 0; display: flex; flex-wrap: wrap; gap: 0.35rem 0.9rem; font-size: 0.74rem; letter-spacing: 0.04em; text-transform: uppercase; color: var(--muted); }
.boat__price { margin: auto 0 0; padding-top: 0.85rem; border-top: 1px solid var(--gold); font-size: 0.85rem; color: var(--inkSoft); display: flex; align-items: baseline; gap: 0.4rem; flex-wrap: wrap; }
.boat__price b { font-family: var(--font-body, var(--body)); font-weight: 600; font-size: 1.25rem; letter-spacing: -0.01em; color: var(--ink); font-variant-numeric: tabular-nums; }

/* ---- concierge: the private desk ----------------------------------------- */
/* The one deliberately dark moment on the page: deep-sea night with gold
   hairlines, so the concierge reads as a private members' desk rather than
   another catalog band. Colours are fixed tokens (night/gold/foam) that stay
   put in both modes. */
.cx {
  position: relative; overflow: hidden; color: var(--foam);
  padding-block: var(--section-y-lg);
  background:
    radial-gradient(90% 70% at 100% 0%, color-mix(in oklch, var(--azure) 38%, var(--night)) 0%, transparent 60%),
    radial-gradient(60% 50% at 0% 100%, color-mix(in oklch, var(--gold) 16%, var(--night)) 0%, transparent 60%),
    linear-gradient(170deg, var(--night2) 0%, var(--night) 100%);
}
.cx::before, .cx::after {
  content: ""; position: absolute; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent 0%, color-mix(in oklch, var(--gold) 55%, transparent) 50%, transparent 100%);
}
.cx::before { top: 0; }
.cx::after { bottom: 0; }
.cx__grid { position: relative; display: grid; grid-template-columns: 1.15fr 0.85fr; gap: clamp(2.5rem, 6vw, 5rem); align-items: center; }
.cx__eyebrow { display: inline-flex; align-items: center; gap: 0.7rem; margin: 0; font-size: 0.72rem; letter-spacing: var(--eyebrow-tracking); text-transform: uppercase; font-weight: 600; color: var(--gold); }
.cx__eyebrow i { width: 2rem; height: 1px; background: var(--gold); }
.cx__title { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: clamp(2.1rem, 3.8vw, 3.3rem); line-height: 1.05; letter-spacing: -0.005em; margin: 0.9rem 0 0; color: var(--foam); max-width: 18ch; text-wrap: balance; }
.cx__lede { margin: 1.1rem 0 0; max-width: 52ch; font-size: 1.08rem; line-height: 1.6; color: var(--foam2); }
.cx__steps { list-style: none; margin: 2.25rem 0 0; padding: 0; }
.cx__step { display: grid; grid-template-columns: 3rem 1fr; gap: 1rem; padding: 1.1rem 0; border-top: 1px solid color-mix(in oklch, var(--gold) 28%, transparent); }
.cx__step:last-child { border-bottom: 1px solid color-mix(in oklch, var(--gold) 28%, transparent); }
.cx__n { font-family: var(--font-display, var(--display)); font-size: 1.5rem; line-height: 1; color: var(--gold); padding-top: 0.15rem; }
.cx__step-title { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: 1.55rem; line-height: 1.08; margin: 0 0 0.3rem; color: var(--foam); }
.cx__step-sub { margin: 0; color: var(--foam2); line-height: 1.5; max-width: 48ch; }
.cx__facts { list-style: none; margin: 1.75rem 0 0; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem 1.5rem; }
.cx__facts li { display: flex; gap: 0.7rem; align-items: flex-start; font-size: 0.92rem; line-height: 1.45; color: var(--foam); }
.cx__facts i { flex: none; width: 0.4rem; height: 0.4rem; border-radius: 50%; background: var(--gold); margin-top: 0.5rem; box-shadow: 0 0 0 3px color-mix(in oklch, var(--gold) 25%, transparent); }
.cx__ctas { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem; margin-top: 2.25rem; }
.cx__or { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--foam2); margin-inline: 0.35rem; }
.cx__sub { margin: 0.9rem 0 0; font-size: 0.85rem; color: var(--foam2); }
.cx__figure { margin: 0; display: grid; gap: 1rem; justify-items: center; }
.cx__arch {
  width: 100%; aspect-ratio: 4 / 5; border-radius: 999px 999px 1.5rem 1.5rem;
  background-size: cover; background-position: center;
  outline: 1px solid color-mix(in oklch, var(--gold) 55%, transparent); outline-offset: 10px;
  box-shadow: 0 50px 90px -50px rgba(0, 0, 0, 0.9);
}
.cx__sig { font-family: var(--font-display, var(--display)); font-style: italic; font-size: 1.02rem; color: var(--foam2); letter-spacing: 0.02em; text-align: center; }

/* ---- ashore: villa panel + transfers itinerary ---------------------------- */
.sec__lede { margin: 0; color: var(--inkSoft); line-height: 1.55; max-width: 36ch; font-size: 1.02rem; }
.ashore { display: grid; grid-template-columns: 1.25fr 1fr; gap: 1.5rem; align-items: stretch; }
.panel__label { display: inline-flex; align-items: center; gap: 0.6rem; margin: 0; font-size: 0.7rem; letter-spacing: var(--eyebrow-tracking); text-transform: uppercase; font-weight: 600; color: var(--gold); }
.panel__label i { width: 1.6rem; height: 1px; background: var(--gold); }
.panel__title { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: clamp(1.9rem, 3vw, 2.5rem); line-height: 1.05; margin: 0.7rem 0 0; color: var(--foam); text-wrap: balance; }
.panel__sub { margin: 0.7rem 0 0; color: var(--foam2); line-height: 1.5; max-width: 46ch; }
.panel__facts { list-style: none; margin: 1.1rem 0 0; padding: 0; display: grid; gap: 0.45rem; }
.panel__facts li { display: flex; gap: 0.65rem; align-items: flex-start; font-size: 0.92rem; line-height: 1.45; color: var(--foam); }
.panel__facts i { flex: none; width: 0.38rem; height: 0.38rem; border-radius: 50%; background: var(--gold); margin-top: 0.5rem; box-shadow: 0 0 0 3px color-mix(in oklch, var(--gold) 25%, transparent); }
.panel__ctas { display: flex; flex-wrap: wrap; align-items: center; gap: 0.9rem; margin-top: 1.5rem; }
.panel__from { font-family: var(--font-body, var(--body)); font-weight: 600; font-size: 0.9rem; color: var(--foam2); font-variant-numeric: tabular-nums; }
.panel__label--ink { color: var(--azure, var(--second)); }
.panel__label--ink i { background: var(--azure, var(--second)); }
.panel__title--ink { color: var(--ink); }
.panel__sub--ink { color: var(--inkSoft); }
.panel__facts--ink li { color: var(--ink); }
.panel__facts--ink i { background: var(--azure, var(--second)); box-shadow: 0 0 0 3px color-mix(in oklch, var(--azure, var(--second)) 20%, transparent); }
.panel__from--ink { color: var(--inkSoft); }

/* Villa: photo panel with the gold hairline frame the concierge arch uses. */
.villa { position: relative; overflow: hidden; border-radius: 1.5rem; min-height: 30rem; display: flex; align-items: flex-end; color: var(--foam); box-shadow: 0 40px 70px -45px color-mix(in oklch, var(--ink) 70%, transparent); }
.villa::after { content: ""; position: absolute; inset: 0.6rem; border-radius: 1.1rem; border: 1px solid color-mix(in oklch, var(--gold) 55%, transparent); pointer-events: none; }
.villa__photo { position: absolute; inset: 0; background-size: cover; background-position: center; transition: transform 700ms ease; }
.villa:hover .villa__photo { transform: scale(1.03); }
.villa__scrim { position: absolute; inset: 0; background: linear-gradient(180deg, color-mix(in oklch, var(--night) 18%, transparent) 0%, color-mix(in oklch, var(--night) 42%, transparent) 42%, color-mix(in oklch, var(--night) 94%, transparent) 100%); }
.villa__txt { position: relative; padding: clamp(1.75rem, 3vw, 2.5rem); }

/* Transfers: an itinerary card on paper, the route drawn as a gold line. */
.ride { position: relative; border-radius: 1.5rem; padding: clamp(1.75rem, 3vw, 2.5rem); background: linear-gradient(170deg, var(--surface) 0%, var(--sky, var(--surfaceAlt)) 100%); border: 1px solid color-mix(in oklch, var(--gold) 45%, var(--line)); display: flex; flex-direction: column; }
.route { list-style: none; margin: 1.5rem 0 0; padding: 0; }
.route li { position: relative; display: grid; grid-template-columns: 1.25rem 1fr; gap: 0.9rem; padding-bottom: 1.1rem; }
.route li:last-child { padding-bottom: 0; }
.route li i { position: relative; z-index: 1; width: 0.7rem; height: 0.7rem; margin-top: 0.4rem; border-radius: 50%; background: var(--surface); border: 2px solid var(--azure, var(--second)); justify-self: center; }
.route li::before { content: ""; position: absolute; left: calc(0.625rem - 0.5px); top: 1.2rem; bottom: -0.1rem; width: 1px; background: color-mix(in oklch, var(--gold) 60%, var(--line)); }
.route li:last-child::before { display: none; }
.route__stop { display: block; font-family: var(--font-display, var(--display)); font-size: 1.4rem; line-height: 1.1; color: var(--ink); }
.route__note { display: block; font-size: 0.82rem; color: var(--muted); margin-top: 0.2rem; }
.ride .panel__ctas { margin-top: auto; padding-top: 1.75rem; }

/* ---- experiences ---------------------------------------------------------- */
.exps { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.6rem; }
/* Flex column + auto-margin foot: every card's price row sits on the same
   baseline no matter how long the title and summary run. */
.exp { display: flex; flex-direction: column; height: 100%; text-decoration: none; color: inherit; }
.exp__frame { position: relative; border-radius: 1.25rem; overflow: hidden; margin-bottom: 1rem; }
.exp__frame::after { content: ""; position: absolute; inset: 0.55rem; border-radius: 0.9rem; border: 1px solid color-mix(in oklch, var(--gold) 55%, transparent); pointer-events: none; }
.exp__photo { aspect-ratio: 4 / 5; background-size: cover; background-position: center; transition: transform 700ms ease; }
.exp__photo--empty { background: linear-gradient(170deg, var(--sky, var(--surfaceAlt)) 0%, color-mix(in oklch, var(--second) 55%, var(--sky, var(--surfaceAlt))) 100%); }
.exp:hover .exp__photo { transform: scale(1.03); }
.exp__meta { display: inline-flex; align-items: center; gap: 0.5rem; margin: 0; font-size: 0.7rem; letter-spacing: var(--eyebrow-tracking); text-transform: uppercase; font-weight: 600; color: var(--gold); }
.exp__meta i { width: 1.2rem; height: 1px; background: var(--gold); }
.exp__title { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: 1.65rem; line-height: 1.1; margin: 0.45rem 0 0; color: var(--ink); }
.exp__sub { margin: 0.4rem 0 0.8rem; font-size: 0.92rem; line-height: 1.5; color: var(--inkSoft); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.exp__title { margin-bottom: 0.1rem; }
.exp__foot { position: relative; display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; margin: auto 0 0; padding-top: 0.45rem; }
/* short gold dash, the same stroke as the eyebrow before the category — not a full-width rule */
.exp__foot::before { content: ""; position: absolute; top: 0; left: 0; width: 1.2rem; height: 1px; background: var(--gold); }
.exp__price { font-family: var(--font-body, var(--body)); font-weight: 600; font-size: 0.95rem; color: var(--ink); font-variant-numeric: tabular-nums; }
.exp__go { font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase; font-weight: 600; color: var(--azure, var(--second)); }

/* ---- we accept ------------------------------------------------------------ */
.accept { padding: clamp(1.75rem, 3.5vw, 2.5rem) 1rem; border-top: 1px solid color-mix(in oklch, var(--gold) 35%, var(--line)); border-bottom: 1px solid color-mix(in oklch, var(--gold) 35%, var(--line)); }

/* ---- hallmarks + contact + partner --------------------------------------- */
.marks { list-style: none; margin: 0; padding: clamp(1.5rem, 3vw, 2.25rem) 0 0; border-top: 1px solid color-mix(in oklch, var(--gold) 45%, var(--line)); display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.5rem 2rem; }
.mark i { display: block; width: 2rem; height: 1px; background: var(--gold); margin-bottom: 0.9rem; }
.mark__title { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: 1.35rem; line-height: 1.15; margin: 0; color: var(--ink); }
.mark__sub { margin: 0.4rem 0 0; font-size: 0.9rem; line-height: 1.5; color: var(--inkSoft); }
.contact { text-align: center; display: grid; gap: 1rem; justify-items: center; padding: clamp(2.5rem, 5vw, 4rem) clamp(1.25rem, 4vw, 3rem); border-radius: 1.5rem; background: linear-gradient(170deg, var(--surface) 0%, var(--sky, var(--surfaceAlt)) 100%); border: 1px solid color-mix(in oklch, var(--gold) 45%, var(--line)); }
.contact__eyebrow { display: inline-flex; align-items: center; gap: 0.7rem; margin: 0; font-size: 0.72rem; letter-spacing: var(--eyebrow-tracking); text-transform: uppercase; font-weight: 600; color: var(--gold); }
.contact__eyebrow i { width: 1.8rem; height: 1px; background: var(--gold); }
.contact__title { max-width: 22ch; margin-top: 0; }
.contact__sub { margin: 0; color: var(--inkSoft); max-width: 44ch; line-height: 1.55; }
.contact__chans { display: flex; flex-wrap: wrap; gap: 0.75rem; justify-content: center; margin-top: 0.5rem; }
.partner { border-top: 1px solid var(--line); padding-block: 1.25rem; font-size: 0.9rem; color: var(--inkSoft); }
.partner__inner { display: flex; flex-wrap: wrap; gap: 0.5rem 1.25rem; justify-content: space-between; align-items: center; }

/* ---- responsive ----------------------------------------------------------- */
@media (max-width: 64rem) {
  .occ { grid-template-columns: repeat(3, 1fr); }
  .boats, .exps, .marks { grid-template-columns: repeat(2, 1fr); }
  .cx__grid { grid-template-columns: 1fr; }
  .cx__facts { grid-template-columns: 1fr; }
  .cx__figure { order: -1; }
  .cx__arch { max-width: 24rem; }
  .ashore { grid-template-columns: 1fr; }
}
@media (max-width: 42rem) {
  .hero { min-height: 32rem; }
  .hero__inner { padding-top: 5rem; padding-bottom: 6rem; }
  .planner { margin-top: -3rem; }
  .planner__card { grid-template-columns: 1fr 1fr; }
  .planner__f:nth-child(2n) { border-right: 0; }
  .planner__go { grid-column: 1 / -1; border-top: 1px solid var(--line); }
  .planner__go .btn { width: 100%; justify-content: center; }
  .sec__head { grid-template-columns: 1fr; align-items: start; }
  .occ, .boats, .exps, .marks { grid-template-columns: 1fr; }
  .villa { min-height: 24rem; }
}
</style>
